import { Avatar, BodyText, InternalLink, PlusIcon, Row, ThinButton } from "$components/DesignSystem"
import React, { useContext } from "react"
import { TouchableOpacity, View, ViewStyle } from "react-native"
import { styled, useTheme } from "styled-rn"

import { AppContext } from "../../../AppContext"
import { useGetUser } from "../../CoreContext"
import { useIsMobile } from "../../hooks/useIsMobile"
import { BASE_MARGIN, AnimatedPressableBase } from "../DesignSystem"
import { PopoverTooltip } from "../popovers/PopoverTooltip"

type Props = {
  users: readonly {
    readonly id: string
    readonly name: string | null
    readonly username: string
    readonly usernameID: string
    avatarURL: string
  }[]
  selectedUser: (username: string, usernameID: string) => void
  showPlus?: boolean
  plusClicked?: () => void
  isShowingInDarkModal?: true
}

export const UsersSelector = (props: Props) => {
  const appUser = useGetUser()
  const sortedUsers = [...props.users].sort((a, b) => a.username.localeCompare(b.username))
  const theme = useTheme()
  const textColor = props.isShowingInDarkModal ? "white" : theme.fg

  const isMobile = useIsMobile()

  return (
    <>
      <Row centerV {...(isMobile ? { style: { flexDirection: "column" } } : {})}>
        {sortedUsers.map((user, i) => {
          const isLoggedIn = user.id === (appUser.type === "user" && appUser.currentUser.id)
          const Outer = (innerProps: { children: any }) =>
            !isLoggedIn ? (
              <TouchableOpacity
                accessibilityHint={`Select to switch user`}
                accessibilityLabel={`Log in as ${user.username}`}
                accessibilityRole="button"
                onPress={() => props.selectedUser(user.username, user.usernameID)}
              >
                {innerProps.children}
              </TouchableOpacity>
            ) : (
              <View>{innerProps.children}</View>
            )

          const Item = isMobile ? HorizontalItem : VerticalItem
          const EditButton = isLoggedIn ? (
            <InternalLink screen="UserProfileSettings" routeOpts={undefined}>
              <ThinButton title="Edit avatar and statistics" bgKey="key" colorKey="keyFG" style={{ marginTop: BASE_MARGIN / 2 }} />
            </InternalLink>
          ) : undefined

          return (
            <Outer key={user.id}>
              <Item user={user} i={i} bold={isLoggedIn} textColor={textColor} footerElement={EditButton} />
            </Outer>
          )
        })}

        {!props.showPlus ? null : (
          <AnimatedPressableBase style={{ marginTop: 16 }} onPress={() => props.plusClicked?.()}>
            <Item>
              <PopoverTooltip text="Create a new player">
                <AddNewPlayerPlus size={120} />
              </PopoverTooltip>
            </Item>
          </AnimatedPressableBase>
        )}
      </Row>
    </>
  )
}

const VerticalItem = (props: { i: number; bold: boolean; user: Props["users"][0]; textColor: string; footerElement?: JSX.Element }) => {
  const theme = useTheme()
  const { user, textColor } = props
  return (
    <Item style={{ marginLeft: props.i === 0 ? 0 : 20 }}>
      <Avatar
        source={{ uri: props.user.avatarURL }}
        size={props.bold ? 144 : 120}
        style={{ borderColor: props.bold ? theme.player : theme.key, marginTop: props.bold ? 0 : 16 }}
      />

      {!user.name ? null : (
        <BodyText style={{ fontFamily: "Zodiak", fontSize: 36, color: textColor, marginTop: 8, marginBottom: 12 }} key={user.name}>
          {user.name}
        </BodyText>
      )}

      <BodyText key={user.id} style={{ marginTop: user.name ? 0 : 12, color: textColor }}>
        @{user.username}
        <BodyText style={{ opacity: 0.5, color: textColor }}>#{user.usernameID}</BodyText>
      </BodyText>

      {props.footerElement ? props.footerElement : null}
    </Item>
  )
}

const HorizontalItem = (props: Parameters<typeof VerticalItem>[0]) => {
  const theme = useTheme()
  const { user, textColor } = props

  return (
    <Item style={{ flexDirection: "row", margin: 0, padding: 0 }}>
      <Avatar
        source={{ uri: props.user.avatarURL }}
        size={props.bold ? 120 : 100}
        style={{ borderColor: props.bold ? theme.player : theme.key, marginTop: props.bold ? 0 : 16 }}
      />

      <View style={{ margin: BASE_MARGIN, marginTop: BASE_MARGIN * 3 }}>
        {!user.name ? null : (
          <BodyText style={{ fontFamily: "Zodiak", fontSize: 36, color: textColor, marginTop: 8, marginBottom: 12 }} key={user.name}>
            {user.name}
          </BodyText>
        )}

        <BodyText key={user.id} style={{ marginTop: user.name ? 0 : 12, color: textColor }}>
          @{user.username}
          <BodyText style={{ opacity: 0.5, color: textColor }}>#{user.usernameID}</BodyText>
        </BodyText>

        {props.footerElement ? props.footerElement : null}
      </View>
    </Item>
  )
}

export const GhostLoadingUserSelectorAvatar = (props: { count: number }) => {
  const theme = useTheme()
  const { environment } = useContext(AppContext)
  return (
    <Row centerV style={{ opacity: 0.5 }}>
      {Array(props.count)
        .fill(0)
        .map((_, i) => (
          <Item key={i}>
            <>
              <Avatar
                source={{ uri: environment.CDNPath("assets/puzicon.png") }}
                size={120}
                style={{ borderColor: theme.fg, marginTop: 0 }}
              />

              <BodyText style={{ fontFamily: "Zodiak", fontSize: 36, color: theme.fg, marginTop: 8, marginBottom: 12 }} key={"name"}>
                ...
              </BodyText>
            </>
          </Item>
        ))}
    </Row>
  )
}

export const AddNewPlayerPlus = (props: { size?: number; style?: ViewStyle }) => {
  const size = props.size || 40

  return (
    <PlusOuter style={{ width: size, height: size, borderRadius: size / 2, ...props.style }}>
      <PlusInner style={{ width: size - 10, height: size - 10, borderRadius: size / 2 }}>
        <PlusIcon size={12} colorKey={"fg"} />
      </PlusInner>
    </PlusOuter>
  )
}

const Item = styled.View({
  display: "flex",
  alignItems: "center",
  margin: 20,
  padding: 10,
})

const PlusOuter = styled.View(({ theme }) => ({
  borderColor: `${theme.fg}22`,
  borderWidth: 3,
  alignContent: "center",
  justifyContent: "center",
  alignItems: "center",
}))

const PlusInner = styled.View(({ theme }) => ({
  backgroundColor: `${theme.fg}22`,
  width: 100,
  height: 100,
  borderRadius: 60,
  alignContent: "center",
  justifyContent: "center",
  alignItems: "center",
}))
